import React, { useEffect, useState } from 'react';
import Modal from 'global/components/modal';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { CoursesSceneTitle, CoursesSceneText } from './styles';
import { Text } from 'global/components/text';
import { get, uniqBy, size, first } from 'global/lib/lodash';
import SnackAlerts from 'global/components/snack-alerts';
import sendData from 'redux/actions/sendData';
import { getErrorMessage, _size } from 'global/utils';
import { useUser } from 'global/hooks/useUser';
import { awaitAll } from 'global/utils/awaitAll';
import Form from 'global/components/form';
import { CenterLoader } from 'global/styles';

export const EditorModal = (props) => {
  const { open } = props;
  const [scene, setScene] = useState(0);
  const [loading, setLoading] = useState();
  const [alerts, setAlerts] = useState();
  const [formState, setFormState] = useState({});
  const [state, setState] = useState({
    allCourseAssignments: [],
    allEditors: [],
  });
  const user = useUser();

  const realm_id = get(user, 'siteContextSelectedRealm.id', get(user, 'me.realm.id'));

  const users = useRetrieveData(
    { url: '/users' },
    {
      build: ['users'],
      params: {
        filter: {
          content_realm_id: realm_id,
        },
      },
      useCache: { reload: 1 },
    }
  );

  const getAllReviewers = async () => {
    try {
      setLoading(true);

      let allAssignments = [];
      Object.keys(props.selected).map((k) => {
        const { userAssignments } = props.selected[k];
        allAssignments = [...allAssignments, ...userAssignments];
      });

      const allCourseAssignments = await awaitAll(
        allAssignments.map((ass) => () => async () => {
          const assign = await sendData(
            {
              url: `/course-assignments/${ass.id}`,
              method: 'get',
            },
            {
              normalize: { build: ['courseAssignments'] },
              params: { include: 'assessment,assessment.editors' },
            }
          );

          return first(assign);
        }),
        { queueSize: 2 }
      ); //.filter(f => !!f.assessment);

      let allEditors = [];
      allCourseAssignments.map((ac) => {
        const { assessment } = ac;
        allEditors = [...allEditors, ...get(assessment, 'editors', [])];
      });

      allEditors = uniqBy(allEditors, 'id');

      setFormState((fs) => ({ ...fs, users: allEditors }));
      setState((s) => ({ ...s, allCourseAssignments, allEditors }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setScene(props.startScene);
      getAllReviewers();
    }
  }, [open]);

  const onClose = (updated = false) => {
    setScene(0);
    setFormState({});
    setState({});
    props.onClose && props.onClose(updated);
  };

  const getSelectedUsers = () => {
    const formUsers = get(formState, 'users', []);
    const obj = {};
    (formUsers || []).map((u) => {
      obj[u.id] = { ...u, reportable: { ...u } };
    });
    return obj;
  };

  const submit = async () => {
    try {
      setLoading(true);

      const formUsers = getSelectedUsers();

      let error;

      await awaitAll(
        state.allCourseAssignments.map((ass) => () => async () => {
          const { assessment } = ass;

          if (!assessment) {
            console.error('No assessment available in course assignment!');
            error = 'No assessment available in course assignment, so unable to add reviewers.';
            return;
          }

          await sendData({
            url: `/assessments/${assessment.id}`,
            method: 'patch',
            data: {
              data: {
                id: assessment.id,
                type: 'assessments',
                relationships: {
                  editors: {
                    data: Object.keys(formUsers).map((k) => ({ type: 'users', id: `${get(formUsers, [k, 'id'])}` })),
                  },
                },
              },
            },
          });
        }),
        { queueSize: 2 }
      );

      if (error) {
        throw new Error(error);
      }

      onClose(true);
      setAlerts({ message: 'Reviewers overwritten successfully!', severity: 'success' });
    } catch (err) {
      console.error(err);
      setAlerts({ message: getErrorMessage(err), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const renderButtons = () => {
    return [
      { title: 'Back', disabled: scene === 0, onClick: () => setScene(scene - 1) },
      { title: 'Next', disabled: scene === 1 || !_size(getSelectedUsers()), onClick: () => setScene(scene + 1) },
      { title: 'Submit', disabled: scene < 1 || loading, onClick: submit },
    ];
  };

  const getText = () => {
    const selCheckLength = size(state.allCourseAssignments);

    const courseTitle = `Overwrite ${selCheckLength > 1 ? 'these' : 'this'} assessment(s) reviewer${selCheckLength > 1 ? 's' : ''}:`;

    const selUsers = getSelectedUsers();

    const userLength = Object.keys(selUsers).length;

    return {
      courseTitle,
      courses: state.allEditors.map((up) => `${up.name} - ${up.email}`).join(', '),
      userTitle: `With ${userLength > 1 ? 'these' : 'this'} ${userLength} reviewer${userLength > 1 ? 's' : ''}:`,
      users: Object.keys(selUsers)
        .map((k) => `${get(selUsers, [k, 'reportable', 'name'])} - ${get(selUsers, [k, 'reportable', 'email'])}`)
        .join(', '),
    };
  };

  const renderScene = () => {
    if (scene === 0) {
      return (
        <>
          {loading && <CenterLoader />}
          {!loading && (
            <Form
              loading={users.loading || loading}
              showAllScreens
              onFormState={setFormState}
              screens={[
                {
                  title: '',
                  inputs: [
                    {
                      id: 'users',
                      type: 'UserSelect',
                      data: users.data,
                      defaultValue: () => get(formState, 'users', state.allEditors),
                      props: {
                        placeholder: 'Select Reviewers',
                      },
                    },
                  ],
                },
              ]}
            />
          )}
        </>
      );
    }

    if (scene === 1) {
      const text = getText();
      return (
        <div>
          <CoursesSceneTitle as={Text} type="title_large">
            {text.courseTitle}
          </CoursesSceneTitle>
          <CoursesSceneText as={Text}>{text.courses}</CoursesSceneText>
          <CoursesSceneTitle as={Text} type="title_large">
            {text.userTitle}
          </CoursesSceneTitle>
          <CoursesSceneText as={Text}>{text.users}</CoursesSceneText>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <Modal open={!!open} onClose={() => onClose()} title={'CHANGE REVIEWERS'} buttons={renderButtons()}>
        {renderScene()}
      </Modal>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </>
  );
};
